import { useCallback } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import type { Tag } from '@/integrations/supabase/types';
import type { ActiveTab } from '@/components/records/types/recordHeader.types';
import type { DialogType } from './useRecordDialogs';
import type { RecordDetails } from './useRecordDetails';

interface DeleteRecord {
  mutate: (id: string) => void;
}

interface Dialogs {
  openDialog: (dialog: DialogType) => void;
  closeDialog: (dialog: DialogType) => void;
}

export interface RecordHandlersParams {
  record: RecordDetails | null;
  localRecord: RecordDetails | null;
  setLocalRecord: (record: RecordDetails) => void;
  updateProcessedText: (text: string) => void;
  navigate: NavigateFunction;
  deleteRecord: DeleteRecord;
  dialogs: Dialogs;
  setActiveTab: (tab: ActiveTab) => void;
  refetchRecord: () => Promise<void>;
}

export interface RecordHandlersReturn {
  handleTagsUpdated: (updatedTags: Tag[]) => void;
  handleReprocessSuccess: (processedText: string) => void;
  handleNavigateToRecords: (tag: Tag) => void;
  handleDeleteConfirm: () => void;
  handleRefetchRecord: () => Promise<void>;
}

export const useRecordHandlers = ({
  record,
  localRecord,
  setLocalRecord,
  updateProcessedText,
  navigate,
  deleteRecord,
  dialogs,
  setActiveTab,
  refetchRecord,
}: RecordHandlersParams) => {
  const handleTagsUpdated = useCallback((updatedTags: Tag[]) => {
    if (localRecord) {
      const updatedRecord = { ...localRecord, tags: updatedTags };
      setLocalRecord(updatedRecord);
    }
  }, [localRecord, setLocalRecord]);

  const handleReprocessSuccess = useCallback((processedText: string) => {
    updateProcessedText(processedText);
    setActiveTab("processed");
  }, [updateProcessedText, setActiveTab]);

  const handleNavigateToRecords = useCallback((tag: Tag) => {
    navigate('/dashboard', { 
      state: { 
        filterByTag: tag.id 
      } 
    });
  }, [navigate]);

  const handleDeleteConfirm = useCallback(() => {
    if (record) {
      deleteRecord.mutate(record.id);
      dialogs.closeDialog('delete');
    }
  }, [record, deleteRecord, dialogs]);

  const handleRefetchRecord = useCallback(async () => {
    await refetchRecord();
  }, [refetchRecord]);

  return {
    handleTagsUpdated,
    handleReprocessSuccess,
    handleNavigateToRecords,
    handleDeleteConfirm,
    handleRefetchRecord,
  };
};